/* ────────────────────────────────────────────────────────────────────
   configurator-share.js
   Shared share module for ALL configurators (rack, pool, default, ...).

   Each configurator snippet provides:
     • <button class="cf-share-btn" data-share-image="..." data-share-product-title="...">
     • Global function window.ConfiguratorShare.getTotalsForShare() that returns
       { totalPrice, totalMaterial, totalExecution, totalHours, dimensions, unit, currency, shareUrl }

   This module:
     • Renders a 1080×1920 portrait share image (template image + banner + QR + logo)
     • Opens a modal with preview + editable caption
     • Offers: Native share (mobile), WhatsApp (universal), Download, Copy link

   No external dependencies — QR generator is embedded.
   ──────────────────────────────────────────────────────────────────── */

(function (global) {
  'use strict';

  /* ─── Brand constants ───────────────────────────────────── */
  var BRAND = {
    gold:        '#D2A878',
    darkText:    '#1F1F1F',
    lightText:   '#FFFFFF',
    bannerBg:    'rgba(31, 31, 31, 0.88)',
    footerBg:    'rgba(255, 255, 255, 0.96)',
    fontFamily:  'Montserrat, system-ui, sans-serif',
    hashtag:     '#theleva'
  };

  /* ─── Canvas dimensions (IG Story / Reels standard) ─────── */
  var CANVAS_W = 1080;
  var CANVAS_H = 1920;

  /* Banner ~20% of canvas height = 384 px. Footer ~8% = 154 px. */
  var BANNER_H = 384;
  var FOOTER_H = 154;
  var IMAGE_H  = CANVAS_H - BANNER_H - FOOTER_H;   /* ~72% */

  /* ─── Locale fallbacks ─────────────────────────────────────
     Labels are pre-translated by the configurator snippet's Liquid and
     exposed via window.ConfiguratorShareLabels. JS only falls back to
     English defaults when the labels object is absent. */
  function t(key, fallback) {
    var L = global.ConfiguratorShareLabels;
    if (L && typeof L[key] === 'string' && L[key]) return L[key];
    return fallback;
  }

  /* ─── Share UI labels ───────────────────────────────────── */
  function labels() {
    return {
      title:           t('title',           'Share your build'),
      defaultCaption:  t('default_caption', 'MY NEXT PROJECT'),
      captionLabel:    t('caption_label',   'Caption'),
      share:           t('share_btn',       'Share'),
      whatsapp:        t('whatsapp_btn',    'WhatsApp'),
      download:        t('download_btn',    'Download image'),
      copyLink:        t('copy_link_btn',   'Copy link'),
      copied:          t('copied',          'Link copied!'),
      generating:      t('generating',      'Generating image...'),
      total:           t('total',           'TOTAL'),
      material:        t('material',        'Material'),
      execution:       t('execution',       'Execution'),
      hours:           t('hours',           'h'),
      close:           t('close',           'Close'),
      imageNotReady:   t('image_not_ready', 'Image not ready'),
      shareCancelled:  t('share_cancelled', 'Share cancelled'),
      copyFailed:      t('copy_failed',     'Copy failed')
    };
  }

  /* ════════════════════════════════════════════════════════════════
     EMBEDDED QR CODE GENERATOR
     Based on Kazuhiko Arase's QR generator (MIT-licensed),
     simplified for binary-mode encoding only.
     ════════════════════════════════════════════════════════════════ */
  var QR = (function () {
    var QRMode = { MODE_8BIT_BYTE: 4 };
    var QRErrorCorrectLevel = { L: 1, M: 0, Q: 3, H: 2 };

    function QRCodeModel(typeNumber, errorCorrectLevel) {
      this.typeNumber = typeNumber;
      this.errorCorrectLevel = errorCorrectLevel;
      this.modules = null;
      this.moduleCount = 0;
      this.dataCache = null;
      this.dataList = [];
    }

    QRCodeModel.prototype = {
      addData: function (data) {
        this.dataList.push(new QR8bitByte(data));
        this.dataCache = null;
      },
      isDark: function (row, col) {
        return this.modules[row][col];
      },
      make: function () {
        this.makeImpl(false, this.getBestMaskPattern());
      },
      makeImpl: function (test, maskPattern) {
        this.moduleCount = this.typeNumber * 4 + 17;
        this.modules = new Array(this.moduleCount);
        for (var row = 0; row < this.moduleCount; row++) {
          this.modules[row] = new Array(this.moduleCount);
          for (var col = 0; col < this.moduleCount; col++) this.modules[row][col] = null;
        }
        this.setupPositionProbePattern(0, 0);
        this.setupPositionProbePattern(this.moduleCount - 7, 0);
        this.setupPositionProbePattern(0, this.moduleCount - 7);
        this.setupPositionAdjustPattern();
        this.setupTimingPattern();
        this.setupTypeInfo(test, maskPattern);
        if (this.typeNumber >= 7) this.setupTypeNumber(test);
        if (this.dataCache == null) {
          this.dataCache = QRCodeModel.createData(this.typeNumber, this.errorCorrectLevel, this.dataList);
        }
        this.mapData(this.dataCache, maskPattern);
      },
      setupPositionProbePattern: function (row, col) {
        for (var r = -1; r <= 7; r++) {
          if (row + r <= -1 || this.moduleCount <= row + r) continue;
          for (var c = -1; c <= 7; c++) {
            if (col + c <= -1 || this.moduleCount <= col + c) continue;
            if ((0 <= r && r <= 6 && (c == 0 || c == 6)) ||
                (0 <= c && c <= 6 && (r == 0 || r == 6)) ||
                (2 <= r && r <= 4 && 2 <= c && c <= 4)) {
              this.modules[row + r][col + c] = true;
            } else {
              this.modules[row + r][col + c] = false;
            }
          }
        }
      },
      getBestMaskPattern: function () {
        var minLostPoint = 0;
        var pattern = 0;
        for (var i = 0; i < 8; i++) {
          this.makeImpl(true, i);
          var lostPoint = QRUtil.getLostPoint(this);
          if (i == 0 || minLostPoint > lostPoint) {
            minLostPoint = lostPoint;
            pattern = i;
          }
        }
        return pattern;
      },
      setupTimingPattern: function () {
        for (var r = 8; r < this.moduleCount - 8; r++) {
          if (this.modules[r][6] != null) continue;
          this.modules[r][6] = (r % 2 == 0);
        }
        for (var c = 8; c < this.moduleCount - 8; c++) {
          if (this.modules[6][c] != null) continue;
          this.modules[6][c] = (c % 2 == 0);
        }
      },
      setupPositionAdjustPattern: function () {
        var pos = QRUtil.getPatternPosition(this.typeNumber);
        for (var i = 0; i < pos.length; i++) {
          for (var j = 0; j < pos.length; j++) {
            var row = pos[i], col = pos[j];
            if (this.modules[row][col] != null) continue;
            for (var r = -2; r <= 2; r++) {
              for (var c = -2; c <= 2; c++) {
                this.modules[row + r][col + c] =
                  (r == -2 || r == 2 || c == -2 || c == 2 || (r == 0 && c == 0));
              }
            }
          }
        }
      },
      setupTypeNumber: function (test) {
        var bits = QRUtil.getBCHTypeNumber(this.typeNumber);
        for (var i = 0; i < 18; i++) {
          var mod = (!test && ((bits >> i) & 1) == 1);
          this.modules[Math.floor(i / 3)][i % 3 + this.moduleCount - 8 - 3] = mod;
        }
        for (var i = 0; i < 18; i++) {
          var mod = (!test && ((bits >> i) & 1) == 1);
          this.modules[i % 3 + this.moduleCount - 8 - 3][Math.floor(i / 3)] = mod;
        }
      },
      setupTypeInfo: function (test, maskPattern) {
        var data = (this.errorCorrectLevel << 3) | maskPattern;
        var bits = QRUtil.getBCHTypeInfo(data);
        for (var i = 0; i < 15; i++) {
          var mod = (!test && ((bits >> i) & 1) == 1);
          if (i < 6) this.modules[i][8] = mod;
          else if (i < 8) this.modules[i + 1][8] = mod;
          else this.modules[this.moduleCount - 15 + i][8] = mod;
        }
        for (var i = 0; i < 15; i++) {
          var mod = (!test && ((bits >> i) & 1) == 1);
          if (i < 8) this.modules[8][this.moduleCount - i - 1] = mod;
          else if (i < 9) this.modules[8][15 - i - 1 + 1] = mod;
          else this.modules[8][15 - i - 1] = mod;
        }
        this.modules[this.moduleCount - 8][8] = (!test);
      },
      mapData: function (data, maskPattern) {
        var inc = -1;
        var row = this.moduleCount - 1;
        var bitIndex = 7;
        var byteIndex = 0;
        for (var col = this.moduleCount - 1; col > 0; col -= 2) {
          if (col == 6) col--;
          while (true) {
            for (var c = 0; c < 2; c++) {
              if (this.modules[row][col - c] == null) {
                var dark = false;
                if (byteIndex < data.length) {
                  dark = (((data[byteIndex] >>> bitIndex) & 1) == 1);
                }
                var mask = QRUtil.getMask(maskPattern, row, col - c);
                if (mask) dark = !dark;
                this.modules[row][col - c] = dark;
                bitIndex--;
                if (bitIndex == -1) {
                  byteIndex++;
                  bitIndex = 7;
                }
              }
            }
            row += inc;
            if (row < 0 || this.moduleCount <= row) {
              row -= inc;
              inc = -inc;
              break;
            }
          }
        }
      }
    };

    QRCodeModel.PAD0 = 0xEC;
    QRCodeModel.PAD1 = 0x11;

    QRCodeModel.createData = function (typeNumber, errorCorrectLevel, dataList) {
      var rsBlocks = QRRSBlock.getRSBlocks(typeNumber, errorCorrectLevel);
      var buffer = new QRBitBuffer();
      for (var i = 0; i < dataList.length; i++) {
        var data = dataList[i];
        buffer.put(data.mode, 4);
        buffer.put(data.getLength(), QRUtil.getLengthInBits(data.mode, typeNumber));
        data.write(buffer);
      }
      var totalDataCount = 0;
      for (var i = 0; i < rsBlocks.length; i++) totalDataCount += rsBlocks[i].dataCount;
      if (buffer.getLengthInBits() > totalDataCount * 8) {
        throw new Error('Code length overflow');
      }
      if (buffer.getLengthInBits() + 4 <= totalDataCount * 8) buffer.put(0, 4);
      while (buffer.getLengthInBits() % 8 != 0) buffer.putBit(false);
      while (true) {
        if (buffer.getLengthInBits() >= totalDataCount * 8) break;
        buffer.put(QRCodeModel.PAD0, 8);
        if (buffer.getLengthInBits() >= totalDataCount * 8) break;
        buffer.put(QRCodeModel.PAD1, 8);
      }
      return QRCodeModel.createBytes(buffer, rsBlocks);
    };

    QRCodeModel.createBytes = function (buffer, rsBlocks) {
      var offset = 0;
      var maxDcCount = 0, maxEcCount = 0;
      var dcdata = new Array(rsBlocks.length);
      var ecdata = new Array(rsBlocks.length);
      for (var r = 0; r < rsBlocks.length; r++) {
        var dcCount = rsBlocks[r].dataCount;
        var ecCount = rsBlocks[r].totalCount - dcCount;
        maxDcCount = Math.max(maxDcCount, dcCount);
        maxEcCount = Math.max(maxEcCount, ecCount);
        dcdata[r] = new Array(dcCount);
        for (var i = 0; i < dcdata[r].length; i++) dcdata[r][i] = 0xff & buffer.buffer[i + offset];
        offset += dcCount;
        var rsPoly = QRUtil.getErrorCorrectPolynomial(ecCount);
        var rawPoly = new QRPolynomial(dcdata[r], rsPoly.getLength() - 1);
        var modPoly = rawPoly.mod(rsPoly);
        ecdata[r] = new Array(rsPoly.getLength() - 1);
        for (var i = 0; i < ecdata[r].length; i++) {
          var modIndex = i + modPoly.getLength() - ecdata[r].length;
          ecdata[r][i] = (modIndex >= 0) ? modPoly.get(modIndex) : 0;
        }
      }
      var totalCodeCount = 0;
      for (var i = 0; i < rsBlocks.length; i++) totalCodeCount += rsBlocks[i].totalCount;
      var data = new Array(totalCodeCount);
      var index = 0;
      for (var i = 0; i < maxDcCount; i++) {
        for (var r = 0; r < rsBlocks.length; r++) {
          if (i < dcdata[r].length) data[index++] = dcdata[r][i];
        }
      }
      for (var i = 0; i < maxEcCount; i++) {
        for (var r = 0; r < rsBlocks.length; r++) {
          if (i < ecdata[r].length) data[index++] = ecdata[r][i];
        }
      }
      return data;
    };

    function QR8bitByte(data) {
      this.mode = QRMode.MODE_8BIT_BYTE;
      this.data = data;
      this.parsedData = [];
      for (var i = 0, len = this.data.length; i < len; i++) {
        var byteArray = [];
        var code = this.data.charCodeAt(i);
        if (code > 0x10000) {
          byteArray[0] = 0xF0 | ((code & 0x1C0000) >>> 18);
          byteArray[1] = 0x80 | ((code & 0x3F000) >>> 12);
          byteArray[2] = 0x80 | ((code & 0xFC0) >>> 6);
          byteArray[3] = 0x80 | (code & 0x3F);
        } else if (code > 0x800) {
          byteArray[0] = 0xE0 | ((code & 0xF000) >>> 12);
          byteArray[1] = 0x80 | ((code & 0xFC0) >>> 6);
          byteArray[2] = 0x80 | (code & 0x3F);
        } else if (code > 0x80) {
          byteArray[0] = 0xC0 | ((code & 0x7C0) >>> 6);
          byteArray[1] = 0x80 | (code & 0x3F);
        } else {
          byteArray[0] = code;
        }
        this.parsedData = this.parsedData.concat(byteArray);
      }
      if (this.parsedData.length != this.data.length) {
        this.parsedData.unshift(191);
        this.parsedData.unshift(187);
        this.parsedData.unshift(239);
      }
    }
    QR8bitByte.prototype = {
      getLength: function () { return this.parsedData.length; },
      write: function (buffer) {
        for (var i = 0, len = this.parsedData.length; i < len; i++) buffer.put(this.parsedData[i], 8);
      }
    };

    var QRUtil = {
      PATTERN_POSITION_TABLE: [
        [], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34],
        [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50], [6, 30, 54], [6, 32, 58], [6, 34, 62],
        [6, 26, 46, 66], [6, 26, 48, 70], [6, 26, 50, 74], [6, 30, 54, 78], [6, 30, 56, 82],
        [6, 30, 58, 86], [6, 34, 62, 90], [6, 28, 50, 72, 94], [6, 26, 50, 74, 98], [6, 30, 54, 78, 102],
        [6, 28, 54, 80, 106], [6, 32, 58, 84, 110], [6, 30, 58, 86, 114], [6, 34, 62, 90, 118],
        [6, 26, 50, 74, 98, 122], [6, 30, 54, 78, 102, 126], [6, 26, 52, 78, 104, 130],
        [6, 30, 56, 82, 108, 134], [6, 34, 60, 86, 112, 138], [6, 30, 58, 86, 114, 142],
        [6, 34, 62, 90, 118, 146], [6, 30, 54, 78, 102, 126, 150], [6, 24, 50, 76, 102, 128, 154],
        [6, 28, 54, 80, 106, 132, 158], [6, 32, 58, 84, 110, 136, 162], [6, 26, 54, 82, 110, 138, 166],
        [6, 30, 58, 86, 114, 142, 170]
      ],
      G15: (1 << 10) | (1 << 8) | (1 << 5) | (1 << 4) | (1 << 2) | (1 << 1) | 1,
      G18: (1 << 12) | (1 << 11) | (1 << 10) | (1 << 9) | (1 << 8) | (1 << 5) | (1 << 2) | 1,
      G15_MASK: (1 << 14) | (1 << 12) | (1 << 10) | (1 << 4) | (1 << 1),
      getBCHTypeInfo: function (data) {
        var d = data << 10;
        while (QRUtil.getBCHDigit(d) - QRUtil.getBCHDigit(QRUtil.G15) >= 0) {
          d ^= (QRUtil.G15 << (QRUtil.getBCHDigit(d) - QRUtil.getBCHDigit(QRUtil.G15)));
        }
        return ((data << 10) | d) ^ QRUtil.G15_MASK;
      },
      getBCHTypeNumber: function (data) {
        var d = data << 12;
        while (QRUtil.getBCHDigit(d) - QRUtil.getBCHDigit(QRUtil.G18) >= 0) {
          d ^= (QRUtil.G18 << (QRUtil.getBCHDigit(d) - QRUtil.getBCHDigit(QRUtil.G18)));
        }
        return (data << 12) | d;
      },
      getBCHDigit: function (data) {
        var digit = 0;
        while (data != 0) { digit++; data >>>= 1; }
        return digit;
      },
      getPatternPosition: function (typeNumber) {
        return QRUtil.PATTERN_POSITION_TABLE[typeNumber - 1];
      },
      getMask: function (maskPattern, i, j) {
        switch (maskPattern) {
          case 0: return (i + j) % 2 == 0;
          case 1: return i % 2 == 0;
          case 2: return j % 3 == 0;
          case 3: return (i + j) % 3 == 0;
          case 4: return (Math.floor(i / 2) + Math.floor(j / 3)) % 2 == 0;
          case 5: return (i * j) % 2 + (i * j) % 3 == 0;
          case 6: return ((i * j) % 2 + (i * j) % 3) % 2 == 0;
          case 7: return ((i * j) % 3 + (i + j) % 2) % 2 == 0;
          default: throw new Error('bad mask: ' + maskPattern);
        }
      },
      getErrorCorrectPolynomial: function (errorCorrectLength) {
        var a = new QRPolynomial([1], 0);
        for (var i = 0; i < errorCorrectLength; i++) {
          a = a.multiply(new QRPolynomial([1, QRMath.gexp(i)], 0));
        }
        return a;
      },
      getLengthInBits: function (mode, type) {
        if (1 <= type && type < 10) return 8;
        if (type < 27) return 16;
        if (type < 41) return 16;
        throw new Error('type: ' + type);
      },
      getLostPoint: function (qrCode) {
        var moduleCount = qrCode.moduleCount;
        var lostPoint = 0;
        for (var row = 0; row < moduleCount; row++) {
          for (var col = 0; col < moduleCount; col++) {
            var sameCount = 0;
            var dark = qrCode.isDark(row, col);
            for (var r = -1; r <= 1; r++) {
              if (row + r < 0 || moduleCount <= row + r) continue;
              for (var c = -1; c <= 1; c++) {
                if (col + c < 0 || moduleCount <= col + c) continue;
                if (r == 0 && c == 0) continue;
                if (dark == qrCode.isDark(row + r, col + c)) sameCount++;
              }
            }
            if (sameCount > 5) lostPoint += (3 + sameCount - 5);
          }
        }
        for (var row = 0; row < moduleCount - 1; row++) {
          for (var col = 0; col < moduleCount - 1; col++) {
            var count = 0;
            if (qrCode.isDark(row, col)) count++;
            if (qrCode.isDark(row + 1, col)) count++;
            if (qrCode.isDark(row, col + 1)) count++;
            if (qrCode.isDark(row + 1, col + 1)) count++;
            if (count == 0 || count == 4) lostPoint += 3;
          }
        }
        for (var row = 0; row < moduleCount; row++) {
          for (var col = 0; col < moduleCount - 6; col++) {
            if (qrCode.isDark(row, col) && !qrCode.isDark(row, col + 1) &&
                qrCode.isDark(row, col + 2) && qrCode.isDark(row, col + 3) &&
                qrCode.isDark(row, col + 4) && !qrCode.isDark(row, col + 5) &&
                qrCode.isDark(row, col + 6)) lostPoint += 40;
          }
        }
        for (var col = 0; col < moduleCount; col++) {
          for (var row = 0; row < moduleCount - 6; row++) {
            if (qrCode.isDark(row, col) && !qrCode.isDark(row + 1, col) &&
                qrCode.isDark(row + 2, col) && qrCode.isDark(row + 3, col) &&
                qrCode.isDark(row + 4, col) && !qrCode.isDark(row + 5, col) &&
                qrCode.isDark(row + 6, col)) lostPoint += 40;
          }
        }
        var darkCount = 0;
        for (var col = 0; col < moduleCount; col++) {
          for (var row = 0; row < moduleCount; row++) {
            if (qrCode.isDark(row, col)) darkCount++;
          }
        }
        var ratio = Math.abs(100 * darkCount / moduleCount / moduleCount - 50) / 5;
        lostPoint += ratio * 10;
        return lostPoint;
      }
    };

    var QRMath = {
      glog: function (n) { if (n < 1) throw new Error('glog ' + n); return QRMath.LOG_TABLE[n]; },
      gexp: function (n) { while (n < 0) n += 255; while (n >= 256) n -= 255; return QRMath.EXP_TABLE[n]; },
      EXP_TABLE: new Array(256),
      LOG_TABLE: new Array(256)
    };
    for (var i = 0; i < 8; i++) QRMath.EXP_TABLE[i] = 1 << i;
    for (var i = 8; i < 256; i++) {
      QRMath.EXP_TABLE[i] = QRMath.EXP_TABLE[i - 4] ^ QRMath.EXP_TABLE[i - 5] ^
                            QRMath.EXP_TABLE[i - 6] ^ QRMath.EXP_TABLE[i - 8];
    }
    for (var i = 0; i < 255; i++) QRMath.LOG_TABLE[QRMath.EXP_TABLE[i]] = i;

    function QRPolynomial(num, shift) {
      if (num.length == undefined) throw new Error(num.length + '/' + shift);
      var offset = 0;
      while (offset < num.length && num[offset] == 0) offset++;
      this.num = new Array(num.length - offset + shift);
      for (var i = 0; i < num.length - offset; i++) this.num[i] = num[i + offset];
    }
    QRPolynomial.prototype = {
      get: function (index) { return this.num[index]; },
      getLength: function () { return this.num.length; },
      multiply: function (e) {
        var num = new Array(this.getLength() + e.getLength() - 1);
        for (var i = 0; i < this.getLength(); i++) {
          for (var j = 0; j < e.getLength(); j++) {
            num[i + j] ^= QRMath.gexp(QRMath.glog(this.get(i)) + QRMath.glog(e.get(j)));
          }
        }
        return new QRPolynomial(num, 0);
      },
      mod: function (e) {
        if (this.getLength() - e.getLength() < 0) return this;
        var ratio = QRMath.glog(this.get(0)) - QRMath.glog(e.get(0));
        var num = new Array(this.getLength());
        for (var i = 0; i < this.getLength(); i++) num[i] = this.get(i);
        for (var i = 0; i < e.getLength(); i++) num[i] ^= QRMath.gexp(QRMath.glog(e.get(i)) + ratio);
        return new QRPolynomial(num, 0).mod(e);
      }
    };

    function QRRSBlock(totalCount, dataCount) {
      this.totalCount = totalCount;
      this.dataCount = dataCount;
    }
    /* RS block table — only L (low EC) included; sufficient for URLs up to ~2,000 chars in Type-15+. */
    QRRSBlock.RS_BLOCK_TABLE = [
      [1, 26, 19], [1, 44, 34], [1, 70, 55], [1, 100, 80], [1, 134, 108],
      [2, 86, 68], [2, 98, 78], [2, 121, 97], [2, 146, 116], [2, 86, 68, 2, 87, 69],
      [4, 101, 81], [2, 116, 92, 2, 117, 93], [4, 133, 107], [3, 145, 115, 1, 146, 116],
      [5, 109, 87, 1, 110, 88], [5, 122, 98, 1, 123, 99], [1, 135, 107, 5, 136, 108],
      [5, 150, 120, 1, 151, 121], [3, 141, 113, 4, 142, 114], [3, 135, 107, 5, 136, 108],
      [4, 144, 115, 4, 145, 116], [2, 139, 111, 7, 140, 112], [4, 151, 121, 5, 152, 122],
      [6, 147, 117, 4, 148, 118], [8, 132, 106, 4, 133, 107], [10, 142, 114, 2, 143, 115],
      [8, 152, 122, 4, 153, 123], [3, 147, 117, 10, 148, 118], [7, 146, 116, 7, 147, 117],
      [5, 145, 115, 10, 146, 116], [13, 145, 115, 3, 146, 116], [17, 145, 115], [17, 145, 115, 1, 146, 116],
      [13, 145, 115, 6, 146, 116], [12, 151, 121, 7, 152, 122], [6, 151, 121, 14, 152, 122],
      [17, 152, 122, 4, 153, 123], [4, 152, 122, 18, 153, 123], [20, 147, 117, 4, 148, 118],
      [19, 148, 118, 6, 149, 119]
    ];
    QRRSBlock.getRSBlocks = function (typeNumber, errorCorrectLevel) {
      var rsBlock = QRRSBlock.RS_BLOCK_TABLE[typeNumber - 1];
      if (rsBlock == undefined) throw new Error('bad rs block @ typeNumber:' + typeNumber);
      var length = rsBlock.length / 3;
      var list = [];
      for (var i = 0; i < length; i++) {
        var count = rsBlock[i * 3 + 0];
        var totalCount = rsBlock[i * 3 + 1];
        var dataCount = rsBlock[i * 3 + 2];
        for (var j = 0; j < count; j++) list.push(new QRRSBlock(totalCount, dataCount));
      }
      return list;
    };

    function QRBitBuffer() { this.buffer = []; this.length = 0; }
    QRBitBuffer.prototype = {
      get: function (index) {
        var bufIndex = Math.floor(index / 8);
        return ((this.buffer[bufIndex] >>> (7 - index % 8)) & 1) == 1;
      },
      put: function (num, length) {
        for (var i = 0; i < length; i++) this.putBit(((num >>> (length - i - 1)) & 1) == 1);
      },
      getLengthInBits: function () { return this.length; },
      putBit: function (bit) {
        var bufIndex = Math.floor(this.length / 8);
        if (this.buffer.length <= bufIndex) this.buffer.push(0);
        if (bit) this.buffer[bufIndex] |= (0x80 >>> (this.length % 8));
        this.length++;
      }
    };

    /* Auto-pick type number that fits the URL */
    function _typeNumberFor(text) {
      var len = unescape(encodeURIComponent(text)).length;
      /* Approx capacity for level L binary mode */
      var caps = [17, 32, 53, 78, 106, 134, 154, 192, 230, 271, 321, 367, 425, 458,
                  520, 586, 644, 718, 792, 858, 929, 1003, 1091, 1171, 1273, 1367,
                  1465, 1528, 1628, 1732, 1840, 1952, 2068, 2188, 2303, 2431, 2563,
                  2699, 2809, 2953];
      for (var i = 0; i < caps.length; i++) if (caps[i] >= len) return i + 1;
      throw new Error('text too long for QR');
    }

    return {
      build: function (text) {
        var type = _typeNumberFor(text);
        var qr = new QRCodeModel(type, QRErrorCorrectLevel.L);
        qr.addData(text);
        qr.make();
        return qr;
      },
      draw: function (qr, ctx, x, y, size, fg, bg) {
        var count = qr.moduleCount;
        var cell = size / count;
        ctx.fillStyle = bg || '#FFFFFF';
        ctx.fillRect(x, y, size, size);
        ctx.fillStyle = fg || '#000000';
        for (var r = 0; r < count; r++) {
          for (var c = 0; c < count; c++) {
            if (qr.isDark(r, c)) {
              ctx.fillRect(
                Math.floor(x + c * cell),
                Math.floor(y + r * cell),
                Math.ceil(cell),
                Math.ceil(cell)
              );
            }
          }
        }
      }
    };
  })();

  /* ════════════════════════════════════════════════════════════════
     IMAGE COMPOSITION (1080×1920 portrait)
     ════════════════════════════════════════════════════════════════ */

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('Image load failed: ' + url)); };
      img.src = url;
    });
  }

  function fmtMoney(value, currency) {
    if (currency === '$' || currency === 'USD') return '$' + Math.round(value);
    if (currency === '€' || currency === 'EUR') return '€' + Math.round(value);
    if (currency === 'Kč' || currency === 'CZK') return Math.round(value) + ' Kč';
    return (currency || '') + Math.round(value);
  }

  /* Compute wrapped text lines without drawing. */
  function _layoutWrappedText(ctx, text, maxWidth, maxLines) {
    var words = String(text || '').split(/\s+/);
    var lines = [];
    var current = '';
    for (var i = 0; i < words.length; i++) {
      var test = current ? current + ' ' + words[i] : words[i];
      if (ctx.measureText(test).width > maxWidth && current) {
        lines.push(current);
        current = words[i];
        if (lines.length >= maxLines - 1) {
          for (var j = i + 1; j < words.length; j++) current += ' ' + words[j];
          break;
        }
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);
    if (lines.length > maxLines) lines = lines.slice(0, maxLines);
    return lines;
  }

  /* Draw a rounded rectangle path (caller calls .fill()/.stroke()). */
  function _drawRoundedRect(ctx, x, y, w, h, r) {
    r = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  /* Draw multi-line text wrapping by width; caps at maxLines. */
  function _drawWrappedText(ctx, text, x, y, maxWidth, lineHeight, maxLines) {
    var words = String(text || '').split(/\s+/);
    var lines = [];
    var current = '';
    for (var i = 0; i < words.length; i++) {
      var test = current ? current + ' ' + words[i] : words[i];
      if (ctx.measureText(test).width > maxWidth && current) {
        lines.push(current);
        current = words[i];
        if (lines.length >= maxLines - 1) {
          /* last line: append remaining words then break */
          for (var j = i + 1; j < words.length; j++) current += ' ' + words[j];
          break;
        }
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);
    /* truncate if exceeded */
    if (lines.length > maxLines) lines = lines.slice(0, maxLines);
    for (var k = 0; k < lines.length; k++) {
      ctx.fillText(lines[k], x, y + k * lineHeight);
    }
  }

  /* Centered variant. Caller sets ctx.textAlign = 'center'. */
  function _drawWrappedTextCentered(ctx, text, centerX, centerY, maxWidth, lineHeight, maxLines) {
    var words = String(text || '').split(/\s+/);
    var lines = [];
    var current = '';
    for (var i = 0; i < words.length; i++) {
      var test = current ? current + ' ' + words[i] : words[i];
      if (ctx.measureText(test).width > maxWidth && current) {
        lines.push(current);
        current = words[i];
        if (lines.length >= maxLines - 1) {
          for (var j = i + 1; j < words.length; j++) current += ' ' + words[j];
          break;
        }
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);
    if (lines.length > maxLines) lines = lines.slice(0, maxLines);
    var startY = centerY - ((lines.length - 1) * lineHeight) / 2;
    for (var k = 0; k < lines.length; k++) {
      ctx.fillText(lines[k], centerX, startY + k * lineHeight);
    }
  }

  function buildShareImage(productImageUrl, totals, qrUrl) {
    var L = labels();
    return loadImage(productImageUrl).then(function (img) {
      var canvas = document.createElement('canvas');
      canvas.width = CANVAS_W;
      canvas.height = CANVAS_H;
      var ctx = canvas.getContext('2d');

      /* ─── 1. WHITE BG (in case image has transparency) ─── */
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      /* ─── 2. PRODUCT IMAGE in top 72% (object-fit: cover) ─── */
      var imgRegionH = IMAGE_H;
      var imgRegionW = CANVAS_W;
      var imgAspect = img.width / img.height;
      var regionAspect = imgRegionW / imgRegionH;
      var drawW, drawH, drawX, drawY;
      if (imgAspect > regionAspect) {
        drawH = imgRegionH;
        drawW = drawH * imgAspect;
        drawX = (imgRegionW - drawW) / 2;
        drawY = 0;
      } else {
        drawW = imgRegionW;
        drawH = drawW / imgAspect;
        drawX = 0;
        drawY = (imgRegionH - drawH) / 2;
      }
      ctx.drawImage(img, drawX, drawY, drawW, drawH);

      /* ─── 3. CAPTION OVERLAY (centered in top third of product image,
            white text on rounded black 25% opacity background) ─── */
      var captionStr = (totals.caption || L.defaultCaption).toUpperCase();
      ctx.font = '900 72px ' + BRAND.fontFamily;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      var captionY = IMAGE_H / 6;
      var captionLineH = 78;
      var captionMaxW = CANVAS_W - 160;

      /* Compute layout (lines + widest line) for background rect */
      var capLines = _layoutWrappedText(ctx, captionStr, captionMaxW, 2);
      var maxLineW = 0;
      for (var i = 0; i < capLines.length; i++) {
        maxLineW = Math.max(maxLineW, ctx.measureText(capLines[i]).width);
      }
      var bgPadX = 40;
      var bgPadY = 22;
      var bgW = maxLineW + bgPadX * 2;
      var bgH = capLines.length * captionLineH + bgPadY * 2 - 14;
      var bgX = (CANVAS_W - bgW) / 2;
      var bgY = captionY - bgH / 2;
      var bgRadius = 28;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
      _drawRoundedRect(ctx, bgX, bgY, bgW, bgH, bgRadius);
      ctx.fill();

      /* White caption text on top */
      ctx.fillStyle = '#FFFFFF';
      var startY = captionY - ((capLines.length - 1) * captionLineH) / 2;
      for (var ln = 0; ln < capLines.length; ln++) {
        ctx.fillText(capLines[ln], CANVAS_W / 2, startY + ln * captionLineH);
      }
      ctx.textAlign = 'left';

      /* ─── 4. BOTTOM BANNER (dark, 20% height, with title + totals + QR) ─── */
      var bannerY = IMAGE_H;
      ctx.fillStyle = BRAND.bannerBg;
      ctx.fillRect(0, bannerY, CANVAS_W, BANNER_H);

      /* Gold accent strip at top of banner */
      ctx.fillStyle = BRAND.gold;
      ctx.fillRect(0, bannerY, CANVAS_W, 5);

      /* QR code on the right side of banner */
      var qrSize = 260;
      var qrPad = 48;
      var qrX = CANVAS_W - qrSize - qrPad;
      var qrY = bannerY + (BANNER_H - qrSize) / 2;
      /* White card behind QR */
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(qrX - 10, qrY - 10, qrSize + 20, qrSize + 20);
      var qr = QR.build(qrUrl);
      QR.draw(qr, ctx, qrX, qrY, qrSize, BRAND.darkText, '#FFFFFF');

      /* Text content on left side of banner */
      var textX = qrPad;
      var textMaxW = qrX - qrPad - 40;
      ctx.textBaseline = 'top';
      ctx.textAlign = 'left';

      /* Product title */
      ctx.fillStyle = BRAND.gold;
      ctx.font = '700 36px ' + BRAND.fontFamily;
      var titleStr = (totals.productTitle || 'PROJECT').toUpperCase();
      ctx.fillText(titleStr, textX, bannerY + 40, textMaxW);

      /* Dimensions */
      ctx.fillStyle = BRAND.lightText;
      ctx.font = '500 28px ' + BRAND.fontFamily;
      var dims = totals.dimensions || {};
      var unitStr = totals.dimensionsUnit || 'm';
      var dimsStr = '';
      if (dims.L != null && dims.W != null && dims.H != null) {
        dimsStr = (+dims.L).toFixed(2) + ' × ' + (+dims.W).toFixed(2) + ' × ' + (+dims.H).toFixed(2) + ' ' + unitStr;
      }
      ctx.fillText(dimsStr, textX, bannerY + 90, textMaxW);

      /* TOTAL big */
      ctx.fillStyle = BRAND.lightText;
      ctx.font = '800 72px ' + BRAND.fontFamily;
      var totalStr = fmtMoney(totals.totalPrice || 0, totals.currency);
      ctx.fillText(totalStr, textX, bannerY + 140, textMaxW);

      ctx.fillStyle = BRAND.gold;
      ctx.font = '500 26px ' + BRAND.fontFamily;
      ctx.fillText(L.total + '  ·  ' + (totals.totalHours || 0) + ' ' + L.hours, textX, bannerY + 222, textMaxW);

      /* Material / Execution breakdown */
      ctx.fillStyle = BRAND.lightText;
      ctx.font = '500 24px ' + BRAND.fontFamily;
      var matStr = L.material + ' ' + fmtMoney(totals.totalMaterial || 0, totals.currency);
      var execStr = L.execution + ' ' + fmtMoney(totals.totalExecution || 0, totals.currency);
      ctx.fillText(matStr + '   ·   ' + execStr, textX, bannerY + 270, textMaxW);

      /* Hashtag bottom of banner */
      ctx.fillStyle = BRAND.gold;
      ctx.font = '700 22px ' + BRAND.fontFamily;
      ctx.fillText(BRAND.hashtag, textX, bannerY + BANNER_H - 50);

      /* ─── 4. FOOTER (light, with logo + URL) ─── */
      var footerY = bannerY + BANNER_H;
      ctx.fillStyle = BRAND.footerBg;
      ctx.fillRect(0, footerY, CANVAS_W, FOOTER_H);

      /* LEVA logo (text-based — no external dep) */
      var logoX = CANVAS_W / 2;
      var logoY = footerY + FOOTER_H / 2;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = BRAND.darkText;
      ctx.font = '700 42px ' + BRAND.fontFamily;
      ctx.fillText('THE LEVA', logoX, logoY - 18);
      ctx.fillStyle = BRAND.gold;
      ctx.font = '400 22px ' + BRAND.fontFamily;
      ctx.fillText('theleva.com', logoX, logoY + 24);
      ctx.textAlign = 'left';

      return canvas;
    });
  }

  /* ════════════════════════════════════════════════════════════════
     MODAL UI
     ════════════════════════════════════════════════════════════════ */

  var _modalEl = null;
  var _lastBlob = null;
  var _lastBlobUrl = null;

  function _isMobile() {
    return /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(navigator.userAgent);
  }

  function _supportsNativeShare() {
    return !!(navigator.share && navigator.canShare);
  }

  function _canShareFiles(file) {
    try {
      return navigator.canShare && navigator.canShare({ files: [file] });
    } catch (e) {
      return false;
    }
  }

  function _ensureModal() {
    if (_modalEl) return _modalEl;
    var L = labels();

    var modal = document.createElement('div');
    modal.className = 'cf-share-modal';
    modal.innerHTML = [
      '<div class="cf-share-backdrop"></div>',
      '<div class="cf-share-dialog">',
      '  <div class="cf-share-header">',
      '    <h3 class="cf-share-title">' + _esc(L.title) + '</h3>',
      '    <button class="cf-share-close" aria-label="' + _esc(L.close) + '">',
      '      <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
      '    </button>',
      '  </div>',
      '  <div class="cf-share-body">',
      '    <div class="cf-share-rail">',
      '      <div class="cf-share-actions">',
      '        <button class="cf-share-act cf-share-act-primary" data-act="native" title="' + _esc(L.share) + '" aria-label="' + _esc(L.share) + '">',
      '          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>',
      '          <span>' + _esc(L.share) + '</span>',
      '        </button>',
      '        <button class="cf-share-act" data-act="whatsapp" title="' + _esc(L.whatsapp) + '" aria-label="' + _esc(L.whatsapp) + '">',
      '          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>',
      '          <span>' + _esc(L.whatsapp) + '</span>',
      '        </button>',
      '        <button class="cf-share-act" data-act="download" title="' + _esc(L.download) + '" aria-label="' + _esc(L.download) + '">',
      '          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>',
      '          <span>' + _esc(L.download) + '</span>',
      '        </button>',
      '        <button class="cf-share-act" data-act="copylink" title="' + _esc(L.copyLink) + '" aria-label="' + _esc(L.copyLink) + '">',
      '          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>',
      '          <span>' + _esc(L.copyLink) + '</span>',
      '        </button>',
      '      </div>',
      '    </div>',
      '    <div class="cf-share-preview-wrap">',
      '      <div class="cf-share-loading">' + _esc(L.generating) + '</div>',
      '      <img class="cf-share-preview" alt="" />',
      '    </div>',
      '    <div class="cf-share-caption-wrap">',
      '      <label class="cf-share-caption-label">' + _esc(L.captionLabel) + '</label>',
      '      <textarea class="cf-share-caption" rows="2"></textarea>',
      '    </div>',
      '    <div class="cf-share-toast" role="status"></div>',
      '  </div>',
      '</div>'
    ].join('');

    document.body.appendChild(modal);
    _modalEl = modal;

    /* close handlers */
    modal.querySelector('.cf-share-close').addEventListener('click', closeModal);
    modal.querySelector('.cf-share-backdrop').addEventListener('click', closeModal);

    /* Hide native share button on platforms without it */
    if (!_supportsNativeShare()) {
      modal.querySelector('[data-act="native"]').style.display = 'none';
    }
    return modal;
  }

  function _esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function _showToast(msg) {
    if (!_modalEl) return;
    var el = _modalEl.querySelector('.cf-share-toast');
    el.textContent = msg;
    el.classList.add('visible');
    clearTimeout(_showToast._t);
    _showToast._t = setTimeout(function () { el.classList.remove('visible'); }, 2400);
  }

  function closeModal() {
    if (!_modalEl) return;
    _modalEl.classList.remove('open');
    document.body.classList.remove('cf-share-open');
    if (_lastBlobUrl) {
      URL.revokeObjectURL(_lastBlobUrl);
      _lastBlobUrl = null;
    }
  }

  function openModal(context) {
    var modal = _ensureModal();
    var L = labels();

    var imgEl = modal.querySelector('.cf-share-preview');
    var loadingEl = modal.querySelector('.cf-share-loading');
    var captionEl = modal.querySelector('.cf-share-caption');
    captionEl.value = L.defaultCaption;

    imgEl.style.display = 'none';
    loadingEl.style.display = 'block';

    modal.classList.add('open');
    document.body.classList.add('cf-share-open');

    /* (Re)build the share image with current caption. */
    function rebuild() {
      loadingEl.style.display = 'block';
      imgEl.style.display = 'none';
      var totalsCopy = {};
      for (var k in context.totals) totalsCopy[k] = context.totals[k];
      totalsCopy.caption = captionEl.value || L.defaultCaption;
      return buildShareImage(context.productImage, totalsCopy, context.shareUrl)
        .then(function (canvas) {
          return new Promise(function (resolve) {
            canvas.toBlob(function (blob) {
              _lastBlob = blob;
              if (_lastBlobUrl) URL.revokeObjectURL(_lastBlobUrl);
              _lastBlobUrl = URL.createObjectURL(blob);
              imgEl.src = _lastBlobUrl;
              imgEl.style.display = 'block';
              loadingEl.style.display = 'none';
              resolve();
            }, 'image/png', 0.92);
          });
        })
        .catch(function (err) {
          loadingEl.textContent = 'Error generating image';
          if (global.console) console.error('[ConfiguratorShare]', err);
        });
    }

    rebuild();

    /* Debounced rebuild on caption edits. */
    var rebuildTimer = null;
    captionEl.oninput = function () {
      clearTimeout(rebuildTimer);
      rebuildTimer = setTimeout(rebuild, 350);
    };

    /* Bind action buttons (rebind every open to capture latest context) */
    var actionEls = modal.querySelectorAll('.cf-share-act');
    actionEls.forEach(function (btn) {
      btn.onclick = function () {
        var act = btn.getAttribute('data-act');
        var caption = captionEl.value || L.defaultCaption;
        _handleAction(act, caption, context);
      };
    });
  }

  function _handleAction(act, caption, context) {
    var L = labels();
    var shareUrl = context.shareUrl;
    var fullText = caption + '\n\n' + shareUrl;

    if (act === 'native') {
      if (!_lastBlob) { _showToast(L.imageNotReady); return; }
      var file = new File([_lastBlob], 'theleva-share.png', { type: 'image/png' });
      var shareData = { text: caption, url: shareUrl };
      if (_canShareFiles(file)) {
        shareData.files = [file];
      }
      navigator.share(shareData).catch(function (err) {
        if (err && err.name !== 'AbortError') {
          _showToast(L.shareCancelled);
        }
      });
    } else if (act === 'whatsapp') {
      var waUrl = 'https://wa.me/?text=' + encodeURIComponent(fullText);
      global.open(waUrl, '_blank');
    } else if (act === 'download') {
      if (!_lastBlobUrl) { _showToast(L.imageNotReady); return; }
      var a = document.createElement('a');
      a.href = _lastBlobUrl;
      a.download = 'theleva-' + (context.productSlug || 'project') + '.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else if (act === 'copylink') {
      _copyToClipboard(shareUrl).then(function () {
        _showToast(L.copied);
      }).catch(function () {
        _showToast(L.copyFailed);
      });
    }
  }

  function _copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        resolve();
      } catch (e) {
        reject(e);
      } finally {
        document.body.removeChild(ta);
      }
    });
  }

  /* ════════════════════════════════════════════════════════════════
     PUBLIC API
     ════════════════════════════════════════════════════════════════ */

  function openShare() {
    if (global.console) console.log('[ConfiguratorShare] openShare() called');
    var btn = document.querySelector('.cf-share-btn');
    var productImage = btn ? btn.getAttribute('data-share-image') : null;
    var productTitle = btn ? btn.getAttribute('data-share-product-title') : '';
    var productSlug = btn ? btn.getAttribute('data-share-product-slug') : 'project';

    if (global.console) {
      console.log('[ConfiguratorShare] btn found:', !!btn);
      console.log('[ConfiguratorShare] productImage:', productImage);
      console.log('[ConfiguratorShare] productTitle:', productTitle);
    }

    if (!productImage) {
      console.warn('[ConfiguratorShare] No share image configured');
      alert('[Share debug] No data-share-image on button. Check Liquid metafield: custom.socials');
      return;
    }

    function fetchTotals() {
      var totals = {};
      if (typeof global.ConfiguratorShareGetTotals === 'function') {
        try { totals = global.ConfiguratorShareGetTotals() || {}; } catch (e) {
          if (global.console) console.error('[ConfiguratorShare] totals hook error:', e);
        }
      }
      return totals;
    }

    /* Wait briefly if totals are still zero (e.g. budget widget hasn't
       finished its first compute yet — common on first share click after
       page load). Polling at 100ms up to 2s. */
    var attempts = 0;
    function tryOpen() {
      var totals = fetchTotals();
      var hasPrice = (totals.totalPrice > 0 || totals.totalMaterial > 0);
      if (hasPrice || attempts >= 20) {
        totals.productTitle = totals.productTitle || productTitle;
        if (global.console) console.log('[ConfiguratorShare] totals (after ' + attempts + ' attempts):', totals);
        var shareUrl = totals.shareUrl || global.location.href;
        openModal({
          productImage:  productImage,
          productTitle:  productTitle,
          productSlug:   productSlug,
          shareUrl:      shareUrl,
          totals:        totals
        });
        return;
      }
      attempts++;
      setTimeout(tryOpen, 100);
    }
    tryOpen();
  }

  function bindAutoOpen() {
    if (global.console) console.log('[ConfiguratorShare] bindAutoOpen — attaching delegated click handler');
    document.addEventListener('click', function (e) {
      var target = e.target;
      while (target && target !== document.body) {
        if (target.classList && target.classList.contains('cf-share-btn')) {
          if (global.console) console.log('[ConfiguratorShare] cf-share-btn click detected on', target);
          e.preventDefault();
          e.stopPropagation();
          openShare();
          return;
        }
        target = target.parentNode;
      }
    }, true);   /* capture phase — runs BEFORE other handlers */
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAutoOpen);
  } else {
    bindAutoOpen();
  }

  global.ConfiguratorShare = {
    open:           openShare,
    close:          closeModal,
    buildImage:     buildShareImage,
    _qr:            QR,
    _version:       '1.0.1-debug'
  };

  if (global.console) {
    console.log('[ConfiguratorShare] loaded v' + global.ConfiguratorShare._version + ' — window.ConfiguratorShare is ready');
  }

})(typeof window !== 'undefined' ? window : this);